import { combineReducers, Reducer } from "redux";
import { ASSOCIATE_DOCUMENT_WITH_STIPULATIONS_SUCCESS, CLEAR_DEALER_SERVICE_STATE, CREATE_DECISION_DENIAL_REASONS_SUCCESS, DealerServiceAction, DELETE_APP_CUSTOMER_STIP_DOCUMENT_ASSOCIATION_SUCCESS, DELETE_DECISION_DENIAL_REASON_SUCCESS, LOAD_INDIRECT_STIPS_AND_DECISION_REASONS_SUCCESS, SET_DEALER_DECISION_CODE, SET_DEALER_SERVICE_COMMENTS, SET_DEALER_SERVICE_STATE, UPDATE_INDIRECT_STIPULATION } from "./actions";
import { IndirectDecisionReason, ApplicationStipulation, DecisionCodeDecisionReasonDefault } from "../../Components/IndirectStipsEditor/IndirectStipulation";
import { intersection } from "ramda";

export interface DealerServiceState {
  decisionCode: string | null;
  decisionComments: string | null;
  applicationStipulations: ApplicationStipulation[] | null;
  adverseActionReasons: IndirectDecisionReason[];
  indirectStipulationDynamicTextDictionary: { [key: number]: string }; // maps the stipCategoryID to the dynamic text to show in the UI
  decisionCodeDecisionReasonDefaults: DecisionCodeDecisionReasonDefault[];
}
export const INITIAL_STATE: DealerServiceState = {
  decisionCode: null,
  decisionComments: null,
  applicationStipulations: null,
  adverseActionReasons: [],
  indirectStipulationDynamicTextDictionary: {},
  decisionCodeDecisionReasonDefaults: []
}

const decisionCodeReducer = (state: string | null = INITIAL_STATE.decisionCode, action: DealerServiceAction): string | null => {
  switch (action.type) {
    case SET_DEALER_DECISION_CODE:
      return action.payload.decisionCode;
    case SET_DEALER_SERVICE_STATE:
      return action.payload.state.decisionCode;
    case CLEAR_DEALER_SERVICE_STATE:
      return INITIAL_STATE.decisionCode;
    default:
      return state;
  }
};

const decisionCommentsReducer = (state: string | null = INITIAL_STATE.decisionComments, action: DealerServiceAction): string | null => {
  switch (action.type) {
    case SET_DEALER_SERVICE_COMMENTS:
      return action.payload.decisionComments;
    case SET_DEALER_SERVICE_STATE:
      return action.payload.state.decisionComments;
    case CLEAR_DEALER_SERVICE_STATE:
      return INITIAL_STATE.decisionComments;
    default:
      return state;
  }
};

// indirectStipulations
const applicationStipulationsReducer = (state: ApplicationStipulation[] | null = INITIAL_STATE.applicationStipulations, action: DealerServiceAction): ApplicationStipulation[] | null => {
  switch (action.type) {
    case LOAD_INDIRECT_STIPS_AND_DECISION_REASONS_SUCCESS:
      return action.payload.applicationStipulations;
    case CLEAR_DEALER_SERVICE_STATE:
      return INITIAL_STATE.applicationStipulations;
    case UPDATE_INDIRECT_STIPULATION:
      if (state) {
        return state.map((stipulation) => {
          if (stipulation.id === action.payload.applicationStipulationId) {
            return {
              ...stipulation,
              statusId: action.payload.statusId,
              note: action.payload.note
            }
          }
          return stipulation;
        })
      }
      return state;
    case ASSOCIATE_DOCUMENT_WITH_STIPULATIONS_SUCCESS:
      // add documents to the indirectStipulation
      if (state) {
        return state.map((stipulation) => {
          // get the list of stipulations for this category. that way we can loop over them and add the document to each one.
          const stipulationsForThisCategory = action.payload.stipulationCategories.find((category) => category.id === stipulation.stipulationCategoryId)?.stipulations ?? [];
          const stipulationIdsForThisCategory = stipulationsForThisCategory.map((s) => s.id);
          // Only add entries for stipulationIds that belong to THIS stipulation's category
          const matchingStipulationIds = action.payload.associationInfo.stipulationIds.filter((id: number) => stipulationIdsForThisCategory.includes(id));
          if (matchingStipulationIds.length > 0) {
            return {
              ...stipulation,
              applicationCustomerStipulationDocuments: [
                ...stipulation.applicationCustomerStipulationDocuments,
                ...matchingStipulationIds.map((stipulationId: number) => {
                  return {
                    id: '', // Temporary — real ID assigned by server; stips are refetched after association
                    stipulationId: stipulationId,
                    documentId: action.payload.associationInfo.documentId
                  }
                })]
            }
          } else {
            return stipulation;
          }
        });
      }
      return state;
    case DELETE_APP_CUSTOMER_STIP_DOCUMENT_ASSOCIATION_SUCCESS:
      if (state) {
        return state.map((stipulation) => {
          if (stipulation.id.toUpperCase() === action.payload.applicationStipulationId.toUpperCase()) {
            return {
              ...stipulation,
              applicationCustomerStipulationDocuments: stipulation.applicationCustomerStipulationDocuments.filter(acsd => acsd.id.toUpperCase() !== action.payload.applicationStipulationDocumentId.toUpperCase())
            }
          }
          return stipulation;
        });
      }
      return state;
    default:
      return state;
  }
};
// indirectDecisionReasons
const adverseActionReasonsReducer = (state: IndirectDecisionReason[] = INITIAL_STATE.adverseActionReasons, action: DealerServiceAction): IndirectDecisionReason[] => {
  switch (action.type) {
    case LOAD_INDIRECT_STIPS_AND_DECISION_REASONS_SUCCESS:
      return action.payload.adverseActionReasons;
    case CLEAR_DEALER_SERVICE_STATE:
      return INITIAL_STATE.adverseActionReasons;
    case CREATE_DECISION_DENIAL_REASONS_SUCCESS:
      if (state) {
        return [...state, ...action.payload.indirectDecisionReasons];
      } else {
        return action.payload.indirectDecisionReasons;
      }
    case DELETE_DECISION_DENIAL_REASON_SUCCESS:
      if (state) {
        return state.filter((reason) => reason.id.toUpperCase() !== action.payload.indirectDecisionDenialReasonId.toUpperCase());
      }
      return state;
    default:
      return state;
  }
}

const indirectStipulationDynamicTextDictionaryReducer = (state: { [key: number]: string } = INITIAL_STATE.indirectStipulationDynamicTextDictionary, action: DealerServiceAction): { [key: number]: string } => {
  switch (action.type) {
    case SET_DEALER_SERVICE_STATE:
      return action.payload.state.indirectStipulationDynamicTextDictionary;
    default:
      return state;
  }
}

const decisionCodeDecisionReasonDefaultsReducer = (state: DecisionCodeDecisionReasonDefault[] = INITIAL_STATE.decisionCodeDecisionReasonDefaults, action: DealerServiceAction): DecisionCodeDecisionReasonDefault[] => {
  switch (action.type) {
    case LOAD_INDIRECT_STIPS_AND_DECISION_REASONS_SUCCESS:
      return action.payload.decisionCodeDecisionReasonDefaults;
    case CLEAR_DEALER_SERVICE_STATE:
      return INITIAL_STATE.decisionCodeDecisionReasonDefaults;
    default:
      return state;
  }
}


export const dealerServiceReducer: Reducer<DealerServiceState> = combineReducers<DealerServiceState>({
  decisionCode: decisionCodeReducer,
  decisionComments: decisionCommentsReducer,
  applicationStipulations: applicationStipulationsReducer,
  adverseActionReasons: adverseActionReasonsReducer,
  indirectStipulationDynamicTextDictionary: indirectStipulationDynamicTextDictionaryReducer,
  decisionCodeDecisionReasonDefaults: decisionCodeDecisionReasonDefaultsReducer
});
